/**
 * Integration Registry - Central source of truth for all hosted integrations.
 *
 * To add a new integration:
 * 1. Create a new folder (e.g., `calendly/`)
 * 2. Create config.ts file with IntegrationDefinition
 * 3. Create EditableFields component if needed
 * 4. Export from the folder's index.ts
 * 5. Add integration type to INTEGRATION_TYPES in types.ts
 * 6. Register in INTEGRATION_REGISTRY below
 */

export * from './types'
export * from './apiConnectionHelpers'
export { Request311EditableFields, DEFAULT_REQUEST311_CONFIG } from './request311'
export { DEFAULT_FASTTRACK_CONFIG } from './fasttrack'
export { DEFAULT_MUNIS_ARWS_CONFIG } from './munis_arws'
export { DEFAULT_CITYWORKS_CONFIG } from './cityworks'
export { DEFAULT_SHAREPOINT_CONFIG } from './sharepoint'
export { DEFAULT_CAYENTA_UTILITY_BILLING_CONFIG } from './cayenta_utility_billing'
export { DEFAULT_OPENGOV_CONFIG } from './opengov'
export { DEFAULT_CENTRAL_SQUARE_CONFIG } from './central_square'
export { DEFAULT_ENERGOV_EPL_CONFIG } from './energov_epl'

import { cayentaUtilityBillingIntegration } from './cayenta_utility_billing'
import { centralSquareIntegration } from './central_square'
import { cityworksIntegration } from './cityworks'
import { energovEplIntegration } from './energov_epl'
import { fasttrackIntegration } from './fasttrack'
import { munisArwsIntegration } from './munis_arws'
import { opengovIntegration } from './opengov'
import { request311Integration } from './request311'
import { sharepointIntegration } from './sharepoint'
import {
  INTEGRATION_TYPES,
  type IntegrationConfig,
  type IntegrationDefinition,
  type IntegrationType,
} from './types'

/**
 * Central registry of all hosted integrations.
 * Components should query this registry rather than hardcoding integration details.
 */
export const INTEGRATION_REGISTRY: Record<IntegrationType, IntegrationDefinition> = {
  [INTEGRATION_TYPES.REQUEST311]: request311Integration,
  [INTEGRATION_TYPES.FASTTRACK]: fasttrackIntegration,
  [INTEGRATION_TYPES.MUNIS_ARWS]: munisArwsIntegration,
  [INTEGRATION_TYPES.CITYWORKS]: cityworksIntegration,
  [INTEGRATION_TYPES.SHAREPOINT]: sharepointIntegration,
  [INTEGRATION_TYPES.CAYENTA_UTILITY_BILLING]: cayentaUtilityBillingIntegration,
  [INTEGRATION_TYPES.OPENGOV]: opengovIntegration,
  [INTEGRATION_TYPES.CENTRAL_SQUARE]: centralSquareIntegration,
  [INTEGRATION_TYPES.ENERGOV_EPL]: energovEplIntegration,
}

/**
 * Get all hosted integrations as an array for rendering.
 */
export const getHostedIntegrations = (): IntegrationDefinition[] => {
  return Object.values(INTEGRATION_REGISTRY)
}

/**
 * Get integration definition by type ID.
 */
export const getIntegrationById = (id: string): IntegrationDefinition | null => {
  return INTEGRATION_REGISTRY[id.toLowerCase() as IntegrationType] || null
}

/**
 * Get the default config for a given integration type.
 */
export const getDefaultIntegrationConfig = (integrationType: string): IntegrationConfig => {
  const integration = getIntegrationById(integrationType)
  return integration?.defaultConfig ? { ...integration.defaultConfig } : {}
}
